"use client";

import * as React from "react";
import Link from "next/link";
import { motion } from "motion/react";
import {
  BookOpen,
  Sparkles,
  ArrowRight,
  Mic,
  Crown,
  Zap,
  Target,
  AlertTriangle,
  Shield,
  Activity,
  Clock,
  TrendingUp,
  Network,
  Compass,
} from "lucide-react";
import { PageHero } from "@/components/ui/page-hero";
import { Section } from "@/components/layout/section";
import { RelatedPages } from "@/components/ui/related-pages";
import { TimelineBlock } from "@/lib/blocks/timeline-block";
import { FeatureGridBlock } from "@/lib/blocks/feature-grid-block";
import { AccordionBlock } from "@/lib/blocks/accordion-block";

const ACCENT: Record<string, { text: string; bg: string; border: string; ring: string; pill: string }> = {
  red: { text: "text-red", bg: "from-red/10", border: "border-red/30", ring: "ring-red/20", pill: "glass-tinted-red border-red/20" },
  purple: { text: "text-purple", bg: "from-purple/10", border: "border-purple/30", ring: "ring-purple/20", pill: "glass-tinted-purple border-purple/20" },
  cyan: { text: "text-cyan", bg: "from-cyan/10", border: "border-cyan/30", ring: "ring-cyan/20", pill: "glass-tinted-cyan border-cyan/20" },
  emerald: { text: "text-emerald", bg: "from-emerald/10", border: "border-emerald/30", ring: "ring-emerald/20", pill: "glass-tinted-emerald border-emerald/20" },
  "amber-gold": { text: "text-amber-gold", bg: "from-amber-gold/10", border: "border-amber-gold/30", ring: "ring-amber-gold/20", pill: "glass-tinted-amber-gold border-amber-gold/20" },
  denim: { text: "text-denim", bg: "from-denim/10", border: "border-denim/30", ring: "ring-denim/20", pill: "glass-tinted-denim border-denim/20" },
  lime: { text: "text-lime", bg: "from-lime/10", border: "border-lime/30", ring: "ring-lime/20", pill: "glass-tinted-lime border-lime/20" },
  coral: { text: "text-coral", bg: "from-coral/10", border: "border-coral/30", ring: "ring-coral/20", pill: "glass-tinted-coral border-coral/20" },
};

export default function CrisisTransformationPage() {
  return (
    <>
      <PageHero
        icon={BookOpen}
        overline="Glossary · Crisis Transformation"
        overlineClassName="text-coral"
        title={
          <>
            Converting Reputational Emergencies Into <span className="text-gradient-premium">Structural Advantage</span>
          </>
        }
        subtitle="The disciplined, systematic process of converting a reputational emergency into a position of structural advantage. Not damage control. Not public relations triage. The engineered reconstruction of narrative, perception, and authority following a threat event — executed at speed, at scale, and with measurable outcomes."
        breadcrumbs={[{ label: "Home", href: "/" }, { label: "Glossary", href: "/glossary/" }, { label: "Crisis Transformation" }]}
        badge="Crisis Response Engine · 384x to 1,416x faster"
      />

      <Section background="default" className="py-16 lg:py-20">
        <div className="max-w-4xl mx-auto">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.coral.pill} mb-5`}>
            <Sparkles className="size-3.5 text-coral" />
            <span className="text-overline text-coral">Definition</span>
          </div>
          <h2 className="text-display-md mb-5">What crisis transformation is.</h2>
          <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
            <span className="font-semibold text-foreground">Crisis transformation</span> is the practice of not merely surviving a reputational crisis but leveraging it as a catalyst for structural narrative improvement. Where traditional crisis management aims to return an entity to its pre-crisis baseline, crisis transformation aims to exceed that baseline — to emerge from the crisis in a stronger, more resilient, and more favorably perceived position than before the threat materialized.
          </p>
          <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
            The distinction is not semantic. It is operational. Crisis management asks: "How does an organization stop the bleeding?" Crisis transformation asks: "How does the entity leverage this event to build something that could not have been built without it?" The former is reactive. The latter is architectural.
          </p>
          <p className="text-body-md text-muted-foreground leading-relaxed">
            In practice, crisis transformation involves the simultaneous execution of three phases: containment (halting the spread of reputational damage), reconstruction (rebuilding narrative authority through coordinated multi-platform deployment), and elevation (positioning the entity above its pre-crisis state through strategic amplification of the response itself).
          </p>
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.red.pill} mb-4`}>
            <Clock className="size-3.5 text-red" />
            <span className="text-overline text-red">The Golden Hour Principle</span>
          </div>
          <h2 className="text-display-md mb-4">Why the first six hours determine everything.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            In crisis transformation, the first six hours after a threat event materializes determine the ultimate trajectory of reputational recovery. Research and operational data reveal a stark reality: 95% of total reputational damage occurs within the first six hours of a crisis.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            { title: "Speed of Detection", body: "The entity must know a crisis is unfolding before the crisis knows it has begun. The intelligence platform provides 72-hour advance warning before competitors detect threats.", accent: "red" },
            { title: "Speed of Assessment", body: "Intelligence without interpretation is noise. The crisis transformation methodology requires rapid threat classification: organic criticism versus coordinated attack, isolated incident versus systemic vulnerability.", accent: "purple" },
            { title: "Speed of Response", body: "Crisis Response Engine activates 500+ pre-built response playbooks within minutes of crisis detection. The platform achieves response speeds 384x to 1,416x faster than traditional crisis management approaches.", accent: "cyan" },
          ].map((p, i) => {
            const a = ACCENT[p.accent];
            return (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`glass-card rounded-2xl p-6 bg-gradient-to-br ${a.bg} to-transparent border ${a.border}`}
              >
                <h3 className={`text-heading-md mb-2 ${a.text}`}>{p.title}</h3>
                <p className="text-body-sm text-muted-foreground leading-relaxed">{p.body}</p>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT["amber-gold"].pill} mb-4`}>
            <Zap className="size-3.5 text-amber-gold" />
            <span className="text-overline text-amber-gold">The Three Phases</span>
          </div>
          <h2 className="text-display-md mb-4">Containment, reconstruction, elevation.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Crisis transformation operates through three simultaneous phases — each essential, each reinforcing the others.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            { num: "01", title: "Containment", body: "Halting the spread of reputational damage. Detection within minutes. Threat classification within seconds. Response generation within two minutes. The 95% of damage that occurs in the first six hours is contained.", accent: "red", icon: Shield },
            { num: "02", title: "Reconstruction", body: "Rebuilding narrative authority through coordinated multi-platform deployment. Counter-narratives deployed across 15+ platforms. Content optimization. Engagement amplification. The rebuilt narrative replaces the original.", accent: "purple", icon: Activity },
            { num: "03", title: "Elevation", body: "Positioning the entity above its pre-crisis state through strategic amplification of the response itself. The crisis becomes evidence of resilience. The response becomes a differentiator. The entity emerges stronger.", accent: "cyan", icon: TrendingUp },
          ].map((p, i) => {
            const a = ACCENT[p.accent];
            return (
              <motion.div
                key={p.num}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group glass-card rounded-2xl p-6 bg-gradient-to-br ${a.bg} to-transparent border ${a.border}`}
              >
                <div className="flex items-start gap-3">
                  <div className={`size-12 rounded-xl bg-gradient-sovereign flex items-center justify-center shadow-lg`}>
                    <p.icon className="size-6 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className={`text-overline ${a.text}`}>Phase {p.num}</span>
                    <h3 className={`text-heading-md mb-2 ${a.text}`}>{p.title}</h3>
                    <p className="text-caption text-muted-foreground leading-relaxed">{p.body}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.denim.pill} mb-4`}>
            <Network className="size-3.5 text-denim" />
            <span className="text-overline text-denim">Related Terms</span>
          </div>
          <h2 className="text-display-md mb-4">The conceptual map.</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            { term: "Perception Management", href: "/glossary/perception-management/", desc: "The Perception pillar that crisis transformation operationalizes.", accent: "red" },
            { term: "Penta P's Framework", href: "/glossary/penta-ps-framework/", desc: "The five-domain model crisis transformation integrates with.", accent: "cyan" },
            { term: "Operational Excellence", href: "/glossary/operational-excellence/", desc: "The engineered system crisis transformation requires.", accent: "amber-gold" },
            { term: "Strategic Sovereignty", href: "/glossary/strategic-sovereignty/", desc: "The integrated discipline crisis transformation supports.", accent: "red" },
            { term: "Sovereign Operations Architecture", href: "/glossary/sovereign-operations-architecture/", desc: "The six-stage methodology that operationalizes the approach.", accent: "emerald" },
            { term: "Digital Conglomerate", href: "/glossary/digital-conglomerate/", desc: "The organizational form that executes crisis transformation.", accent: "purple" },
          ].map((t, i) => {
            const a = ACCENT[t.accent];
            return (
              <motion.div
                key={t.href}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
              >
                <Link href={t.href} className="block h-full group">
                  <div className={`relative glass-card rounded-2xl p-5 h-full bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all`}>
                    <h3 className={`text-heading-sm mb-2 ${a.text}`}>{t.term}</h3>
                    <p className="text-caption text-muted-foreground leading-relaxed mb-3">{t.desc}</p>
                    <span className={`inline-flex items-center gap-1.5 text-caption font-semibold ${a.text} group-hover:gap-2.5 transition-all`}>
                      Read <ArrowRight className="size-3" />
                    </span>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-10">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT["amber-gold"].pill} mb-4`}>
            <Clock className="size-3.5 text-amber-gold" />
            <span className="text-overline text-amber-gold">Crisis Timeline</span>
          </div>
          <h2 className="text-display-md mb-4">The crisis transformation timeline.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Six milestones from crisis detection through post-recovery positioning. The timeline maps the operational logic that turns reputational emergencies into structural advantage.
          </p>
        </div>
        <TimelineBlock
          milestones={[
            { period: "T-72h", title: "Pre-Crisis Intelligence", body: "The intelligence platform detects emerging threats 24-72 hours before competitors identify them. Pre-crisis positioning prepares the operational response before the crisis materializes.", accent: "cyan" },
            { period: "T+0 to T+2min", title: "Detection & Classification", body: "Crisis detection within 2 minutes versus 4-72 hours traditionally. Threat classification within 30 seconds. The Golden Hour Principle is engaged.", accent: "purple" },
            { period: "T+2 to T+5min", title: "Response Generation", body: "Response generation within 2 minutes. 500+ pre-built playbooks activated based on threat type. Counter-narrative directives issued across the integrated platform ecosystem.", accent: "emerald" },
            { period: "T+5min to T+24h", title: "Multi-Platform Deployment", body: "Coordinated multi-platform deployment within 5 minutes. Counter-narratives deployed across 15+ platforms. Content optimization. Engagement amplification. Containment achieved within the first 6 hours.", accent: "amber-gold" },
            { period: "T+24h to T+30d", title: "Reconstruction Phase", body: "Multi-week reconstruction phase. Narrative authority rebuilt through coordinated deployment. Stakeholder confidence restored. Operational continuity maintained throughout the recovery period.", accent: "red" },
            { period: "T+30d to T+90d", title: "Elevation Phase", body: "The entity emerges stronger than the pre-crisis baseline. The crisis becomes evidence of resilience. The response becomes a differentiator. The transformation is complete.", accent: "coral" },
          ]}
        />
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-10">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.denim.pill} mb-4`}>
            <Compass className="size-3.5 text-denim" />
            <span className="text-overline text-denim">Usage Examples</span>
          </div>
          <h2 className="text-display-md mb-4">Crisis transformation in practice.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Four examples of crisis transformation applied across different threat categories. Each example shows the methodology producing structural advantage.
          </p>
        </div>
        <FeatureGridBlock
          features={[
            { title: "Executive Misconduct Allegation", body: "An executive under misconduct allegations. Detection within minutes. Response within the Golden Hour. The crisis response engine deploys the Executive Crisis playbook. The narrative is contained, the entity emerges with the executive's successor positioned as the future.", accent: "red" },
            { title: "Data Privacy Incident", body: "A data breach affecting millions of users. Detection within minutes. Assessment within 30 seconds. The Data/Privacy Crisis playbook activates. Containment, communication, and remediation occur in coordinated unity. The entity emerges with stronger security architecture.", accent: "purple" },
            { title: "Product Safety Recall", body: "A product recall affecting global operations. Detection within minutes. Response generation within 2 minutes. The Product/Service Crisis playbook coordinates across 18 countries. Customer communication, regulatory engagement, and media response are synchronized.", accent: "cyan" },
            { title: "Coordinated Narrative Attack", body: "A coordinated narrative attack designed to damage institutional reputation. Pre-crisis intelligence identifies the attack vector. The General Reputation Crisis playbook activates. Counter-narrative deployment reverses the attack within 72 hours.", accent: "emerald" },
          ]}
          columns={2}
        />
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-10">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT["amber-gold"].pill} mb-4`}>
            <BookOpen className="size-3.5 text-amber-gold" />
            <span className="text-overline text-amber-gold">Common Misconceptions</span>
          </div>
          <h2 className="text-display-md mb-4">What crisis transformation is not.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Six persistent misconceptions that conflate crisis transformation with conventional crisis management. The distinctions are operational, not rhetorical.
          </p>
        </div>
        <AccordionBlock
          items={[
            { q: "Is crisis transformation the same as crisis management?", a: "No. Crisis management aims to return an entity to its pre-crisis baseline. Crisis transformation aims to exceed the baseline — to emerge from the crisis in a stronger, more resilient, and more favorably perceived position. The distinction is operational, not rhetorical." },
            { q: "Is crisis transformation the same as crisis communications?", a: "No. Crisis communications focuses on messaging. Crisis transformation focuses on outcomes. The communications are an instrument; the outcomes are the objective. Messaging without operational depth produces empty statements." },
            { q: "Does crisis transformation require pre-existing crisis conditions?", a: "No. Crisis transformation can be applied preventatively — building the operational architecture before the crisis materializes. Pre-crisis intelligence and pre-built playbooks allow the response to engage within minutes, not days." },
            { q: "Is crisis transformation a public relations discipline?", a: "No. Crisis transformation is an operational discipline. Public relations is one instrument. The full methodology includes intelligence, security, perception, politics, policing, and policy coordination. PR alone cannot produce structural advantage." },
            { q: "Can crisis transformation be applied to non-reputational crises?", a: "Yes. The methodology applies to operational crises, financial crises, regulatory crises, environmental crises, and any threat event that requires coordinated multi-platform response. The 12 crisis categories cover the full spectrum." },
            { q: "Does crisis transformation require proprietary technology?", a: "Proprietary technology is the substrate that enables sovereign-grade crisis transformation. The crisis response engine, the intelligence platform, and the perception operations require capabilities that commercial tools cannot provide. The methodology can be approximated without the technology — but the outcomes degrade accordingly." },
          ]}
        />
      </Section>

      <Section background="default" className="py-20 lg:py-28">
        <div className="relative">
          <div aria-hidden className="absolute -top-20 -left-20 w-96 h-96 rounded-full bg-coral/15 blur-3xl pointer-events-none" />
          <div aria-hidden className="absolute -bottom-20 -right-20 w-96 h-96 rounded-full bg-amber-gold/15 blur-3xl pointer-events-none" />
          <div className="relative glass-strong rounded-3xl p-8 md:p-12 lg:p-16 gradient-border text-center">
            <Zap className="size-10 text-coral mx-auto mb-4" />
            <h2 className="text-display-md mb-3">Engage crisis transformation.</h2>
            <p className="text-body-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              Crisis transformation is available through sovereign-scale engagements — where the response speed and operational depth match the stakes. National security level confidentiality from first contact.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                href="/contact/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md bg-gradient-sovereign text-white font-semibold shadow-lg hover:scale-105 transition-transform"
              >
                <Mic className="size-4" />
                Begin an Engagement
                <ArrowRight className="size-4" />
              </Link>
              <Link
                href="/glossary/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md glass font-semibold hover:scale-105 transition-transform"
              >
                <BookOpen className="size-4" />
                Glossary Hub
              </Link>
            </div>
          </div>
        </div>
      </Section>

      <RelatedPages
        title="Continue exploring"
        items={[
          { title: "Glossary Hub", description: "Ten terms defining sovereign-scale operations.", href: "/glossary/", icon: BookOpen, accent: "cyan" },
          { title: "About Dr. Jyoti Kush", description: "The full story of the operational architect.", href: "/about/", icon: Crown, accent: "red" },
          { title: "FAQ: Platforms", description: "The crisis transformation engine and other platforms.", href: "/faq/platforms/", icon: Target, accent: "amber-gold" },
          { title: "Perception Management", description: "The Perception pillar in operational detail.", href: "/glossary/perception-management/", icon: BookOpen, accent: "red" },
        ]}
      />
    </>
  );
}
